import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowEventApiService } from '../services/workflow-event-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { WorkflowQueueItemResponse } from '../models/workflow-runtime.model';
import { workflowObjectTypeLabel, workflowSourceModuleLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-event-queue',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './workflow-event-queue.component.html'
})
export class WorkflowEventQueueComponent implements OnInit, OnDestroy {
  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). Se usan como
   * RESPALDO de los campos `*Label` que resuelve el backend — cuando el DTO no los trae, la
   * plantilla pintaba el enum del motor en crudo.
   */
  readonly objectTypeText = workflowObjectTypeLabel;

  items: WorkflowQueueItemResponse[] = [];
  loading = false;
  processing = false;
  error = '';

  // Filtros
  filterStatus = '';
  filterQueueType = '';

  // Payload modal
  showPayloadModal = false;
  selectedPayload: Record<string, any> | null = null;

  // Totales
  totalElements = 0;
  currentPage = 0;
  totalPages = 0;

  /**
   * INC_CRM_020 (7) inc24 — los dos filtros listaban la clave técnica cruda del backend
   * (PENDING, CRM_EVENT, INBOUND_WEBHOOK…): inglés y jerga de base de datos en una UI en
   * español. El `value` sigue siendo la clave —es lo que viaja al API— y solo cambia lo que
   * se lee. La descripción explica qué encola cada tipo, que era la otra queja: nombres
   * poco claros.
   */
  readonly statusOptions: ReadonlyArray<{ value: string; label: string }> = [
    { value: '',          label: 'Todos los estados' },
    { value: 'PENDING',   label: 'Encolado' },
    { value: 'PROCESSED', label: 'Procesado' },
    { value: 'IGNORED',   label: 'Ignorado' },
    { value: 'FAILED',    label: 'Fallido' }
  ];

  readonly queueTypeOptions: ReadonlyArray<{ value: string; label: string }> = [
    { value: '',                label: 'Todos los tipos' },
    { value: 'CRM_EVENT',       label: 'Evento del CRM' },
    { value: 'INBOUND_WEBHOOK', label: 'Webhook entrante' },
    { value: 'DATE_TRIGGER',    label: 'Disparador por fecha' },
    { value: 'DELAY',           label: 'Espera programada' },
    { value: 'CONDITION_WAIT',  label: 'Espera por condición' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private eventApi: WorkflowEventApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.loadQueue();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  loadQueue(): void {
    this.loading = true;
    this.error = '';
    const filters = {
      businessId: this.businessId,
      ...(this.filterStatus    && { status: this.filterStatus }),
      ...(this.filterQueueType && { queueType: this.filterQueueType })
    };
    this.eventApi.getQueue(filters).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data;
        this.totalElements = data.length;
        this.totalPages = 1;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la cola de eventos.';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadQueue();
  }

  processPending(): void {
    this.processing = true;
    this.error = '';
    this.eventApi.processPending().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const processed = res.processed ?? 0;
        if (processed === 0) {
          this.toast.info({ title: 'Sin pendientes', message: 'No había eventos pendientes por procesar.' });
        } else {
          const eventTypes = (res.items ?? [])
            .map(i => i.eventType)
            .filter((t): t is string => !!t);
          const detail = eventTypes.length > 0 ? `: ${eventTypes.join(', ')}` : '';
          this.toast.success({
            title: 'Procesamiento completado',
            message: `Se ${processed === 1 ? 'procesó 1 evento pendiente' : `procesaron ${processed} eventos pendientes`}${detail}.`
          });
        }
        this.processing = false;
        this.loadQueue();
      },
      error: (err) => {
        this.error = err.status === 403
          ? 'No tienes permisos para realizar esta acción.'
          : (err.error?.message || 'Error al procesar pendientes.');
        this.processing = false;
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  openPayload(item: WorkflowQueueItemResponse): void {
    this.selectedPayload = item.payload ?? {};
    this.showPayloadModal = true;
  }

  get payloadJson(): string {
    return JSON.stringify(this.selectedPayload, null, 2);
  }

  getStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'PENDING':   return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'PROCESSED': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'IGNORED':   return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
      case 'FAILED':    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:          return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    }
  }

  getStatusLabel(status: string): string {
    const found = this.statusOptions.find(o => o.value && o.value === status?.toUpperCase());
    return found ? found.label : (status || '—');
  }

  /**
   * INC_CRM_020 (7) inc24 — la columna "Tipo" pintaba el enum crudo (CRM_EVENT). La clave
   * técnica se conserva en el `title` para depurar sin sacrificar la legibilidad, igual que
   * ya se hizo con la columna "Evento" en la ronda (6).
   */
  getQueueTypeLabel(queueType: string): string {
    const found = this.queueTypeOptions.find(o => o.value && o.value === queueType?.toUpperCase());
    return found ? found.label : (queueType || '—');
  }

  /**
   * Módulo que originó el evento. El mapa vive en el diccionario único del módulo
   * (`models/workflow-labels.ts`) para no mantener dos copias que se desincronicen.
   */
  getSourceModuleLabel(sourceModule: string | null | undefined): string {
    return workflowSourceModuleLabel(sourceModule);
  }

  get pendingCount(): number {
    return this.items.filter(i => i.status === 'PENDING').length;
  }

  get failedCount(): number {
    return this.items.filter(i => i.status === 'FAILED').length;
  }
}
